import React from "react";
import { Box, Button, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { useAuth } from "../../common/auth";
import { C } from "../../common/theme";
import { formatDate } from "../../common/constant/dates";
import { SellerApplication, approveSellerApplication } from "../../common/requests/sellers/endpoints";
import { Column, ConfirmDialog, DataTable, Resource, Section, StatusPill, errorMessage, useToast } from "../../ui";
import { APPLICATION_STATUS, pendingCount, visibleApplications } from "./applicationData";

export type { SellerApplication };

const clamp = (lines: number) => ({ display: "-webkit-box", WebkitLineClamp: lines, WebkitBoxOrient: "vertical" as const, overflow: "hidden" });

/** Buyers apply to sell from their own account on the marketplace (/f/seller);
 * approving here makes that account a seller and emails them. Shown as the
 * "Seller applications" tab in Users & Sellers, which owns the list so the
 * tab can show the pending count. */
export default function SellerApplications({ applications }: { applications: Resource<SellerApplication[]> }) {
  const { idToken, hasPermission } = useAuth();
  const toast = useToast();
  const canApprove = hasPermission("seller_creation_create");
  const [filter, setFilter] = React.useState<"pending" | "all">("pending");
  const [target, setTarget] = React.useState<SellerApplication | null>(null);
  const [approving, setApproving] = React.useState(false);
  const [approveError, setApproveError] = React.useState<string | null>(null);

  const all = applications.data ?? [];
  const rows = visibleApplications(all, filter);
  const pending = pendingCount(all) ?? 0;

  const approve = async () => {
    if (!idToken || !target) return;
    setApproving(true);
    setApproveError(null);
    try {
      await approveSellerApplication(target.application_id, idToken);
      toast(`${target.business_name} can now sell. They've been emailed.`);
      setTarget(null);
      await applications.reload();
    } catch (err) {
      setApproveError(errorMessage(err, "Couldn't approve this seller."));
    } finally {
      setApproving(false);
    }
  };

  const columns: Column<SellerApplication>[] = [
    {
      key: "applicant",
      header: "Applicant",
      card: "title",
      sortValue: (a) => a.name.toLowerCase(),
      render: (a) => (
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontWeight: 600 }}>{a.name}</Box>
          <Box sx={{ fontSize: 12.5, fontWeight: 400, color: C.muted, overflowWrap: "anywhere" }}>{a.email}</Box>
          {a.phone_number && <Box sx={{ fontSize: 12.5, fontWeight: 400, color: C.muted }}>{a.phone_number}</Box>}
        </Box>
      ),
    },
    {
      key: "business",
      header: "Business",
      card: "subtitle",
      render: (a) => (
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ color: C.ink, fontWeight: 600 }}>{a.business_name}</Box>
          <Box sx={{ fontSize: 12.5, color: C.muted }}>{a.country ?? "–"}</Box>
        </Box>
      ),
    },
    {
      key: "about",
      header: "About",
      card: "subtitle",
      width: "38%",
      render: (a) => (
        <Tooltip title={a.business_description} placement="top-start">
          <Typography variant="body2" sx={{ ...clamp(3), color: C.ink2, overflowWrap: "anywhere" }}>
            {a.business_description}
          </Typography>
        </Tooltip>
      ),
    },
    {
      key: "submitted",
      header: "Submitted",
      sortValue: (a) => new Date(a.submitted_at).getTime(),
      render: (a) => <Box sx={{ whiteSpace: "nowrap" }}>{formatDate(a.submitted_at)}</Box>,
    },
    {
      key: "status",
      header: "Status",
      card: "aside",
      render: (a) => <StatusPill tone={APPLICATION_STATUS[a.status].tone}>{APPLICATION_STATUS[a.status].label}</StatusPill>,
    },
    {
      key: "action",
      header: "",
      cardLabel: "Action",
      align: "right",
      render: (a) =>
        a.status === "pending" ? (
          <Button
            variant="contained"
            disabled={!canApprove}
            onClick={(e) => {
              e.stopPropagation();
              setApproveError(null);
              setTarget(a);
            }}
            sx={{ minHeight: 44, width: { xs: "100%", md: "auto" } }}
          >
            Approve
          </Button>
        ) : a.status === "no_account" ? (
          <Tooltip title="They applied before accounts were required. Ask them to sign up with this email, then approve.">
            <Typography variant="caption" sx={{ color: C.muted }}>
              Needs account: ask them to sign up with this email
            </Typography>
          </Tooltip>
        ) : (
          <Box component="span" sx={{ color: C.faint }}>
            –
          </Box>
        ),
    },
  ];

  return (
    <Section
      title="Seller applications"
      subtitle={pending ? `${pending} waiting for review` : applications.data ? "Nothing waiting for review" : undefined}
      actions={
        <ToggleButtonGroup exclusive value={filter} onChange={(_, v) => v && setFilter(v)} aria-label="Which applications">
          <ToggleButton value="pending" sx={{ minHeight: 44, px: 2 }}>
            To review
          </ToggleButton>
          <ToggleButton value="all" sx={{ minHeight: 44, px: 2 }}>
            All
          </ToggleButton>
        </ToggleButtonGroup>
      }
    >
      <DataTable
        aria-label="Seller applications"
        rows={rows}
        columns={columns}
        getRowId={(a) => String(a.application_id)}
        loading={applications.status === "loading" || applications.status === "idle"}
        error={applications.error}
        onRetry={applications.reload}
        initialSort={{ key: "submitted", dir: "desc" }}
        empty={filter === "pending" ? "No applications waiting for review." : "No applications yet."}
      />

      <ConfirmDialog
        open={target !== null}
        title={`Approve ${target?.business_name ?? ""}?`}
        body={`${target?.name ?? ""} (${target?.email ?? ""}) will be able to list plants straight away at the default 10% commission, and will get an email letting them know.`}
        confirmLabel="Approve"
        busy={approving}
        error={approveError}
        onConfirm={approve}
        onClose={() => setTarget(null)}
      />
    </Section>
  );
}
